import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatDate } from "../utils/dateFormat";
import { browserToday } from "../features/vendorInvoices/paymentOnReceiptForm";

/**
 * The month calendar that opens under a typed date box (DateInput, and
 * EditableCell's date mode). Picking a day hands back ISO yyyy-mm-dd; the box
 * itself still shows and reads dd/mm/yyyy, so typing keeps working exactly as
 * before (D109.6) and the calendar is only a second way to fill it.
 *
 * Not a native <input type="date"> picker: that follows the browser locale,
 * which is the reason the typed box exists. Rendered in a portal with fixed
 * positioning so a grid's overflow does not clip it.
 *
 * The calendar cancels mousedown, so clicking a day never blurs the box:
 * a box that commits on blur must not commit half-typed text first. Its
 * mousedown also stops at the calendar: it lives in a portal, so a filter panel
 * that closes on an outside mousedown (Quotations, Confirmed orders) would
 * otherwise read a day click as "outside" and unmount the box before the pick.
 */
type Props = {
  anchor: HTMLElement | null;
  /** ISO yyyy-mm-dd, or "" / null for none. */
  value: string | null | undefined;
  min?: string;
  max?: string;
  onPick: (iso: string) => void;
  onClose: () => void;
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const WIDTH = 252;
const HEIGHT = 300;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoOf(year: number, month0: number, day: number): string {
  return `${year}-${pad2(month0 + 1)}-${pad2(day)}`;
}

function monthOf(iso: string | null | undefined): { year: number; month0: number } {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso || "");
  if (m) return { year: Number(m[1]), month0: Number(m[2]) - 1 };
  const d = new Date();
  return { year: d.getFullYear(), month0: d.getMonth() };
}

function place(anchor: HTMLElement): { top: number; left: number } {
  const r = anchor.getBoundingClientRect();
  const below = r.bottom + 4;
  const top = below + HEIGHT > window.innerHeight && r.top - HEIGHT - 4 >= 0 ? r.top - HEIGHT - 4 : below;
  const left = Math.max(4, Math.min(r.left, window.innerWidth - WIDTH - 4));
  return { top, left };
}

export default function CalendarPopover({ anchor, value, min, max, onPick, onClose }: Props) {
  const [view, setView] = useState(() => monthOf(value));
  const rootRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(() => (anchor ? place(anchor) : null));

  // Follow the box when the page or a grid scrolls under it.
  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => setPos(place(anchor));
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor]);

  // A date typed into the box moves the calendar to that month.
  useEffect(() => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value || "")) setView(monthOf(value));
  }, [value]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Native listener, so it runs before document-level outside-click handlers.
  // Stopping here also hides the mousedown from React's own listener on the
  // portal container, so this listener keeps the box focused itself.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const stop = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener("mousedown", stop);
    return () => el.removeEventListener("mousedown", stop);
  }, [pos]);

  if (!anchor || !pos) return null;

  const { year, month0 } = view;
  const firstWeekday = new Date(year, month0, 1).getDay();
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  const today = browserToday();
  const outOfRange = (iso: string) => Boolean((min && iso < min) || (max && iso > max));

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const shift = (delta: number) => {
    const m = month0 + delta;
    setView({ year: year + Math.floor(m / 12), month0: ((m % 12) + 12) % 12 });
  };

  const navBtn = "h-7 w-7 rounded text-slate-600 hover:bg-slate-100";

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Choose date"
     
      style={{ position: "fixed", top: pos.top, left: pos.left, width: WIDTH, zIndex: 1000 }}
      className="rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700 shadow-lg"
    >
      <div className="mb-1 flex items-center justify-between">
        <button type="button" className={navBtn} aria-label="Previous year" onClick={() => shift(-12)}>
          «
        </button>
        <button type="button" className={navBtn} aria-label="Previous month" onClick={() => shift(-1)}>
          ‹
        </button>
        <span className="flex-1 text-center text-sm font-semibold text-slate-800">
          {MONTHS[month0]} {year}
        </span>
        <button type="button" className={navBtn} aria-label="Next month" onClick={() => shift(1)}>
          ›
        </button>
        <button type="button" className={navBtn} aria-label="Next year" onClick={() => shift(12)}>
          »
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1 font-medium text-slate-400">
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={`blank-${i}`} />;
          const iso = isoOf(year, month0, d);
          const selected = iso === value;
          const disabled = outOfRange(iso);
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              aria-label={formatDate(iso)}
              aria-pressed={selected}
             
              onClick={() => onPick(iso)}
              className={[
                "h-8 rounded",
                selected
                  ? "bg-primary-600 font-semibold text-white"
                  : iso === today
                  ? "font-semibold text-primary-600 ring-1 ring-primary-500 hover:bg-slate-100"
                  : "hover:bg-slate-100",
                disabled ? "cursor-not-allowed text-slate-300 hover:bg-transparent" : "",
              ].join(" ")}
            >
              {d}
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between border-t border-slate-100 pt-1">
        <button
          type="button"
          disabled={outOfRange(today)}
          className="rounded px-2 py-1 font-medium text-primary-600 hover:bg-slate-100 disabled:text-slate-300"
         
          onClick={() => onPick(today)}
        >
          Today
        </button>
        <button type="button" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}
